import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { supabase } from "../../lib/supabase";
import { categories } from "../../mocks/categories";

interface CompanyItem {
  id: string;
  name: string;
  name_kr?: string;
  logo_url?: string;
  website?: string;
  tagline_vi?: string;
  tagline_kr?: string;
  main_category_name?: string;
  main_category_name_kr?: string;
  category_id?: string;
  logo_from?: string;
  logo_to?: string;
  logo_icon?: string;
  logo_initials?: string;
  founded?: string;
  location?: string;
}

const CompaniesPage = () => {
  const { i18n } = useTranslation();
  const isKr = i18n.language === "kr";
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState<string>("all");
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const { data, count } = await supabase
          .from("companies")
          .select("id, name, name_kr, logo_url, website, tagline_vi, tagline_kr, main_category_name, main_category_name_kr, category_id, logo_from, logo_to, logo_icon, logo_initials, founded, location", { count: "exact" })
          .order("created_at", { ascending: false });
        if (data) {
          setCompanies(data as CompanyItem[]);
          setTotalCount(count ?? data.length);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const filtered = companies.filter((c) => {
    const matchSearch =
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.name_kr ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (c.main_category_name ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (c.tagline_vi ?? "").toLowerCase().includes(search.toLowerCase());

    const matchCat =
      selectedCat === "all" ||
      c.category_id === selectedCat ||
      (c.main_category_name ?? "").toLowerCase().includes(
        (categories.find((cat) => cat.id === selectedCat)?.name ?? "").toLowerCase()
      );

    return matchSearch && matchCat;
  });

  const catName = (cat: { name: string; nameKr: string }) => isKr ? cat.nameKr : cat.name;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#001428] via-[#002a5c] to-[#004A99] py-16">
          <div className="max-w-[1400px] mx-auto px-6 text-center">
            <span className="inline-block bg-white/15 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-4 whitespace-nowrap">
              🇰🇷 {isKr ? "한국 파트너 기업" : "Doanh Nghiệp Đối Tác Hàn Quốc"}
            </span>
            <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
              {isKr ? "한국기업 소개" : "Giới Thiệu Doanh Nghiệp"}
            </h1>
            <p className="text-white/70 max-w-xl mx-auto mb-8 text-sm leading-relaxed">
              {isKr
                ? "베트남 시장에 진출하는 한국 첨단 기술 기업들을 소개합니다."
                : "Các doanh nghiệp công nghệ tiên tiến Hàn Quốc đang tiến vào thị trường Việt Nam."}
            </p>
            {/* Search */}
            <div className="max-w-md mx-auto">
              <div className="flex h-12 bg-white rounded-xl border border-white/20 overflow-hidden">
                <div className="w-12 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line text-lg" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={isKr ? "기업명, 카테고리 검색..." : "Tìm kiếm doanh nghiệp, danh mục..."}
                  className="flex-1 text-sm text-gray-700 outline-none placeholder-gray-400 bg-transparent"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Category filter */}
        <div className="bg-white border-b border-gray-100 sticky top-[64px] z-10">
          <div className="max-w-[1400px] mx-auto px-6 py-3 flex gap-2 overflow-x-auto">
            <button
              onClick={() => setSelectedCat("all")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                selectedCat === "all" ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-600 hover:bg-[#004A99]/10 hover:text-[#004A99]"
              }`}
            >
              {isKr ? "전체" : "Tất cả"}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCat(cat.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  selectedCat === cat.id ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-600 hover:bg-[#004A99]/10 hover:text-[#004A99]"
                }`}
              >
                {catName(cat)}
              </button>
            ))}
          </div>
        </div>

        {/* Company grid */}
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          {/* Count label - right aligned, above grid */}
          <div className="flex justify-end mb-4">
            <p className="text-xs text-gray-400">
              {isKr ? `${filtered.length}개 기업 표시 중` : `Hiển thị ${filtered.length} doanh nghiệp`}
            </p>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse flex flex-col items-center gap-3">
                  <div className="w-20 h-20 rounded-2xl bg-gray-100" />
                  <div className="h-3 w-3/4 bg-gray-100 rounded" />
                  <div className="h-2.5 w-1/2 bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-search-line text-5xl text-gray-300 mb-4 block" />
              <p className="text-gray-500">
                {isKr ? "검색 결과가 없습니다." : "Không tìm thấy kết quả."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
              {filtered.map((company) => (
                <Link
                  key={company.id}
                  to={`/doanh-nghiep/${company.id}`}
                  className="group bg-white rounded-2xl border border-gray-100 hover:border-[#004A99]/30 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden flex flex-col items-center p-5 text-center"
                >
                  {/* Logo */}
                  <div
                    className="w-28 h-28 rounded-2xl flex items-center justify-center mb-3 overflow-hidden flex-shrink-0"
                    style={
                      company.logo_url
                        ? { background: "#fff" }
                        : { background: `linear-gradient(135deg, ${company.logo_from ?? "#004A99"}, ${company.logo_to ?? "#00AEEF"})` }
                    }
                  >
                    {company.logo_url ? (
                      <img
                        src={company.logo_url}
                        alt={company.name}
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center">
                        <i className={`${company.logo_icon ?? "ri-building-2-line"} text-white text-3xl`} />
                        {company.logo_initials && (
                          <span className="text-white font-black text-xs mt-0.5">{company.logo_initials}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Name */}
                  <h3 className="font-bold text-[#212529] text-xs leading-tight group-hover:text-[#004A99] transition-colors line-clamp-2 mb-1">
                    {company.name}
                  </h3>
                  {company.name_kr && (
                    <p className="text-[10px] text-gray-400 mb-2 line-clamp-1">{company.name_kr}</p>
                  )}

                  {/* Category badge */}
                  {(company.main_category_name || company.main_category_name_kr) && (
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#004A99]/8 text-[#004A99] font-semibold mb-2 line-clamp-1 max-w-full">
                      {isKr ? (company.main_category_name_kr ?? company.main_category_name) : company.main_category_name}
                    </span>
                  )}

                  {/* Website */}
                  {company.website && (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] text-[#00AEEF] hover:underline flex items-center gap-1 mt-auto truncate max-w-full"
                    >
                      <i className="ri-external-link-line text-[10px] flex-shrink-0" />
                      <span className="truncate">
                        {company.website.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}
                      </span>
                    </a>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CompaniesPage;
